import {
  type AdminCancelSessionRequestBody,
  type AdminReassignCoachRequestBody,
  type AdminReassignCoachResponseBody,
  type AdminRescheduleSessionRequestBody,
  buildAdminAbandonSessionRequest,
  buildAdminCancelSessionRequest,
  buildAdminClearExternalReviewRequest,
  buildAdminGetQuotaRequest,
  buildAdminQuotaGrantRequest,
  buildAdminReassignCoachRequest,
  buildAdminRescheduleSessionRequest,
  buildAdminResolveSessionRequest,
  parseAdminAbandonSessionResponse,
  parseAdminCancelSessionResponse,
  parseAdminClearExternalReviewResponse,
  parseAdminGetQuotaResponse,
  parseAdminQuotaGrantResponse,
  parseAdminReassignCoachResponse,
  parseAdminRescheduleSessionResponse,
  parseAdminResolveSessionResponse,
} from '@efdigital/ll-admin-v2/data/builders/coaching/admin-sessions';
import type AppSessionService from '@efdigital/ll-admin-v2/services/session';
import type {
  AdminPostAbandonRequestBody,
  AdminPostAbandonResponseBody,
  AdminPostQuotaGrantRequestBody,
  AdminPostQuotaGrantResponseBody,
  AdminPostResolveRequestBody,
  AdminPostResolveResponseBody,
  CoachingPlanQuotaSummary,
} from '@efdigital/ll-cdk-coaching-sessions-api-types';
import Service, { service } from '@ember/service';

export default class AdminSessionsService extends Service {
  @service declare session: AppSessionService;

  async resolveSession(
    sessionId: string,
    body: AdminPostResolveRequestBody
  ): Promise<AdminPostResolveResponseBody> {
    const request = await buildAdminResolveSessionRequest(
      this.session,
      sessionId,
      body
    );
    const response = await fetch(request);
    return parseAdminResolveSessionResponse(response);
  }

  async abandonSession(
    sessionId: string,
    body: AdminPostAbandonRequestBody = {}
  ): Promise<AdminPostAbandonResponseBody> {
    const request = await buildAdminAbandonSessionRequest(
      this.session,
      sessionId,
      body
    );
    const response = await fetch(request);
    return parseAdminAbandonSessionResponse(response);
  }

  async cancelSession(
    sessionId: string,
    body: AdminCancelSessionRequestBody
  ): Promise<void> {
    const request = await buildAdminCancelSessionRequest(
      this.session,
      sessionId,
      body
    );
    const response = await fetch(request);
    return parseAdminCancelSessionResponse(response);
  }

  async rescheduleSession(
    sessionId: string,
    body: AdminRescheduleSessionRequestBody
  ): Promise<void> {
    const request = await buildAdminRescheduleSessionRequest(
      this.session,
      sessionId,
      body
    );
    const response = await fetch(request);
    return parseAdminRescheduleSessionResponse(response);
  }

  async clearExternalReview(sessionId: string): Promise<void> {
    const request = await buildAdminClearExternalReviewRequest(
      this.session,
      sessionId
    );
    const response = await fetch(request);
    return parseAdminClearExternalReviewResponse(response);
  }

  async grantQuota(
    planId: string,
    idempotencyKey: string,
    body: AdminPostQuotaGrantRequestBody
  ): Promise<AdminPostQuotaGrantResponseBody> {
    const request = await buildAdminQuotaGrantRequest(
      this.session,
      planId,
      idempotencyKey,
      body
    );
    const response = await fetch(request);
    return parseAdminQuotaGrantResponse(response);
  }

  async getQuota(planId: string): Promise<CoachingPlanQuotaSummary> {
    const request = await buildAdminGetQuotaRequest(this.session, planId);
    const response = await fetch(request);
    return parseAdminGetQuotaResponse(response);
  }

  /**
   * Moves a coachee onto a different coach, or off their coach entirely.
   *
   * Omit `newCoachUserId` to unassign only. Every live session with the outgoing
   * coach is cancelled or abandoned server-side before the move, and everyone
   * affected is emailed.
   */
  async reassignCoach(
    planId: string,
    body: AdminReassignCoachRequestBody
  ): Promise<AdminReassignCoachResponseBody> {
    const request = await buildAdminReassignCoachRequest(
      this.session,
      planId,
      body
    );
    const response = await fetch(request);
    return parseAdminReassignCoachResponse(response);
  }
}

declare module '@ember/service' {
  interface Registry {
    'admin-sessions': AdminSessionsService;
  }
}
